import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, Eye, EyeOff, CheckCircle } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";
import Sidebar from "@/components/Sidebar";
import DashboardFooter from "@/components/DashboardFooter";
import BillingCountrySettings from "@/components/BillingCountrySettings";
import { getClientAuthHeaders, getClientAuthToken, isImpersonating } from "@/lib/apiAuth";

const AccountSettings = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const impersonating = isImpersonating();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleCurrentPasswordChange = (e) => {
    const value = e.target.value;
    setCurrentPassword(value);
    setApiError("");
    setSuccess(false);
    
    if (touched.currentPassword) {
      if (!value) {
        setErrors((prev) => ({ ...prev, currentPassword: "Current password is required" }));
      } else {
        setErrors((prev) => ({ ...prev, currentPassword: "" }));
      }
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setApiError("");
    setSuccess(false);
    
    if (touched.newPassword) {
      if (!value) {
        setErrors((prev) => ({ ...prev, newPassword: "New password is required" }));
      } else if (value.length < 6) {
        setErrors((prev) => ({ ...prev, newPassword: "Password must be at least 6 characters" }));
      } else {
        setErrors((prev) => ({ ...prev, newPassword: "" }));
      }
    }

    // Also validate confirm password if it's been touched
    if (touched.confirmNewPassword && confirmNewPassword) {
      if (value !== confirmNewPassword) {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "Passwords do not match" }));
      } else {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "" }));
      }
    }
  };

  const handleConfirmNewPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmNewPassword(value);
    setApiError("");
    setSuccess(false);
    
    if (touched.confirmNewPassword) {
      if (!value) {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "Please confirm your new password" }));
      } else if (value !== newPassword) {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "Passwords do not match" }));
      } else {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "" }));
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    if (field === "currentPassword") {
      if (!currentPassword) {
        setErrors((prev) => ({ ...prev, currentPassword: "Current password is required" }));
      } else {
        setErrors((prev) => ({ ...prev, currentPassword: "" }));
      }
    }
    
    if (field === "newPassword") {
      if (!newPassword) {
        setErrors((prev) => ({ ...prev, newPassword: "New password is required" }));
      } else if (newPassword.length < 6) {
        setErrors((prev) => ({ ...prev, newPassword: "Password must be at least 6 characters" }));
      } else {
        setErrors((prev) => ({ ...prev, newPassword: "" }));
      }
    }

    if (field === "confirmNewPassword") {
      if (!confirmNewPassword) {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "Please confirm your new password" }));
      } else if (confirmNewPassword !== newPassword) {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "Passwords do not match" }));
      } else {
        setErrors((prev) => ({ ...prev, confirmNewPassword: "" }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ currentPassword: true, newPassword: true, confirmNewPassword: true });
    
    // Validate
    const newErrors = {};
    if (!currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }
    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }
    if (!confirmNewPassword) {
      newErrors.confirmNewPassword = "Please confirm your new password";
    } else if (confirmNewPassword !== newPassword) {
      newErrors.confirmNewPassword = "Passwords do not match";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setApiError("");
    setSuccess(false);

    try {
      const token = getClientAuthToken();
    if (!token) {
        setApiError("You are not logged in. Please login and try again.");
        setIsLoading(false);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_new_password: confirmNewPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data;

      if (data.status === "error") {
        setApiError(data.message || "Failed to change password. Please try again.");
        setIsLoading(false);
        return;
      }

      // Success
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setTouched({});
      setErrors({});
      setIsLoading(false);
    } catch (err) {
      console.error("Change password error:", err);
      
      if (err.response) {
        if (err.response.status === 401) {
          setApiError("Session expired. Please login again.");
          // Optionally redirect to login
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else {
          const errorMessage = err.response.data?.detail || err.response.data?.message || "Failed to change password. Please try again.";
          setApiError(errorMessage);
        }
      } else if (err.request) {
        setApiError("Network error. Please check your connection and try again.");
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }
      setIsLoading(false);
    }
  };

  const isValid = currentPassword && newPassword && newPassword.length >= 6 && confirmNewPassword && newPassword === confirmNewPassword;

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="account-settings-page">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Container with left margin for sidebar (desktop only) */}
      <div className="flex-1 md:ml-64 flex flex-col">
        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-lg animate-fade-in-up">
            {/* Page Title */}
            <div className="mb-8">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
                Account Settings
              </h1>
              <p className="text-[#9CA3AF] font-body text-sm">
                Manage your account preferences and security
              </p>
            </div>

            <BillingCountrySettings />

            {/* Change Password Card */}
            {impersonating ? (
              <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
                <div className="mb-2">
                  <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-2 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#00D1FF]" />
                    Password changes disabled
                  </h2>
                  <p className="text-[#9CA3AF] font-body text-sm">
                    You are managing this client account on their behalf. Password changes are not
                    available during partner console access.
                  </p>
                </div>
              </div>
            ) : (
            <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
              {/* Section Header */}
              <div className="mb-8">
                <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-2 flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#00D1FF]" />
                Change your password
              </h2>
              <p className="text-[#9CA3AF] font-body text-sm">
                Update your password to keep your account secure
              </p>
            </div>

            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30" data-testid="success-message">
                <p className="text-green-400 text-sm font-body text-center flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Password changed successfully!
                </p>
              </div>
            )}

            {/* API Error */}
            {apiError && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="api-error">
                <p className="text-red-400 text-sm font-body text-center">{apiError}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Current Password Input */}
              <div className="space-y-2">
                <label htmlFor="current-password-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                  <Lock className="w-4 h-4 text-[#00D1FF]" />
                  Current Password
                </label>
                <div className="relative">
                  <input
                    id="current-password-input"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={handleCurrentPasswordChange}
                    onBlur={() => handleBlur("currentPassword")}
                    placeholder="Enter your current password"
                    disabled={isLoading}
                    className={`w-full bg-white/5 border rounded-lg px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                      errors.currentPassword && touched.currentPassword
                        ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                        : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                    }`}
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.currentPassword && touched.currentPassword && (
                  <p className="text-red-400 text-xs font-body">{errors.currentPassword}</p>
                )}
              </div>

              {/* New Password Input */}
              <div className="space-y-2">
                <label htmlFor="new-password-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                  <Lock className="w-4 h-4 text-[#00D1FF]" />
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="new-password-input"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    onBlur={() => handleBlur("newPassword")}
                    placeholder="Enter your new password"
                    disabled={isLoading}
                    className={`w-full bg-white/5 border rounded-lg px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                      errors.newPassword && touched.newPassword
                        ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                        : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                    }`}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.newPassword && touched.newPassword && (
                  <p className="text-red-400 text-xs font-body">{errors.newPassword}</p>
                )}
                <p className="text-[#9CA3AF]/70 text-xs font-body">
                  Must be at least 6 characters long
                </p>
              </div>

              {/* Confirm New Password Input */}
              <div className="space-y-2">
                <label htmlFor="confirm-password-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                  <Lock className="w-4 h-4 text-[#00D1FF]" />
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="confirm-password-input"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmNewPassword}
                    onChange={handleConfirmNewPasswordChange}
                    onBlur={() => handleBlur("confirmNewPassword")}
                    placeholder="Confirm your new password"
                    disabled={isLoading}
                    className={`w-full bg-white/5 border rounded-lg px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                      errors.confirmNewPassword && touched.confirmNewPassword
                        ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                        : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                    }`}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmNewPassword && touched.confirmNewPassword && (
                  <p className="text-red-400 text-xs font-body">{errors.confirmNewPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isValid || isLoading}
                className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 mt-8 ${
                  isValid && !isLoading
                    ? "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                    : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                }`}
                data-testid="change-password-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Change Password
                  </>
                )}
              </button>
            </form>
          </div>
            )}
        </div>
      </main>

        <DashboardFooter />
      </div>
    </div>
  );
};

export default AccountSettings;
